import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, Container, CssBaseline, Toolbar, Typography, AppBar as MuiAppBar, Chip } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import React, { useEffect, useState } from 'react';
import { constants, ROUTES } from '../constants';
import { useLocation } from 'react-router-dom';
import { ConfigContext, useSettingsData, usePageImage, useHeaderImage } from './ConfigContext';
import { useThemeDetails } from './ThemeDetailsContext';
import LogoutModal from './LogoutModal';
import { LogoAndProductName } from './LogoAndProductName/LogoAndProductName';
import NotificationModal from './NotificationModal';
import Cookies from 'js-cookie';
import { PermissionGate } from './PermissionGate';
import { RBAC_ACTIONS } from '@/utils/permissionGateConfig';
import { ConfigurationAPI } from '../apis';
const drawerWidth = 240;
const AppBar = styled(MuiAppBar, {
    shouldForwardProp: (prop) => prop !== 'open'
})(({ theme, open }) => ({
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(['width', 'margin'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen
    }),
    ...(open && {
        marginLeft: drawerWidth,
        width: `calc(100% - ${drawerWidth}px)`,
        transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen
        })
    })
}));
// Application navigation is now handled by the ProductSelection page
// Icons and navigation items are no longer needed in the header
export default function MiniDrawer({ children }) {
    const sanitizeUrl = (url) => {
        if (!url)
            return '';
        const lower = url.toLowerCase().trim();
        // Intentional XSS guard: block javascript:, data:, and vbscript: pseudo-protocols
        if (lower.startsWith('javascript:') || // NOSONAR typescript:S5334
            lower.startsWith('data:') ||
            lower.startsWith('vbscript:'))
            return '';
        return url;
    };
    const theme = useTheme();
    const { configState, setConfigState } = React.useContext(ConfigContext);
    const [application, setApplication] = useState(configState.selectedProduct);
    const [userRole, setUserRole] = useState('');
    const { pathname } = useLocation();
    const [isLoading, setIsLoading] = useState(true);
    const [showHeader, setShowHeader] = useState(true);
    const [showFooter, setShowFooter] = useState(true);
    const [footerBrandName, setFooterBrandName] = useState('Converge™ by Deloitte');
    const [footerCopyrightText, setFooterCopyrightText] = useState('Copyright © Deloitte Development LLC ');
    const [footerPrivacyUrl, setFooterPrivacyUrl] = useState('');
    const [footerTermsUrl, setFooterTermsUrl] = useState(ROUTES.TERMS_OF_USE);
    const [footerCookiesUrl, setFooterCookiesUrl] = useState('');
    const [clickedIcon, setClickedIcon] = useState(null);
    const notificationRef = React.useRef(null);
    const profileRef = React.useRef(null);
    // Cached data hooks
    const getCachedSettings = useSettingsData();
    const getCachedPageImage = usePageImage();
    const getCachedHeaderImage = useHeaderImage();
    const { fetchThemeDetails } = useThemeDetails();
    const getIndicatorBackgroundColor = React.useCallback((iconType) => {
        if (clickedIcon !== iconType)
            return 'transparent';
        const isWhiteBackground = configState?.settings?.header_background_color === '#FFFFFF';
        return isWhiteBackground ? '#BB271A' : '#FFFFFF';
    }, [clickedIcon, configState?.settings?.header_background_color]);
    const renderIndicator = (ref, iconType) => {
        if (!ref.current)
            return null;
        return (_jsx(Box, { sx: {
                position: 'absolute',
                bottom: 0,
                left: ref.current.offsetLeft,
                width: '32px',
                height: '3px',
                backgroundColor: getIndicatorBackgroundColor(iconType),
                transition: 'background-color 0.3s ease',
                borderRadius: '2px 2px 0 0'
            } }));
    };
    useEffect(() => {
        const handleNotificationModalOpen = () => setClickedIcon('notification');
        const handleNotificationModalClose = () => setClickedIcon(null);
        const handleProfileModalOpen = () => setClickedIcon('profile');
        const handleProfileModalClose = () => setClickedIcon(null);
        globalThis.window.addEventListener('notificationModalOpen', handleNotificationModalOpen);
        globalThis.window.addEventListener('notificationModalClose', handleNotificationModalClose);
        globalThis.window.addEventListener('profileModalOpen', handleProfileModalOpen);
        globalThis.window.addEventListener('profileModalClose', handleProfileModalClose);
        return () => {
            globalThis.window.removeEventListener('notificationModalOpen', handleNotificationModalOpen);
            globalThis.window.removeEventListener('notificationModalClose', handleNotificationModalClose);
            globalThis.window.removeEventListener('profileModalOpen', handleProfileModalOpen);
            globalThis.window.removeEventListener('profileModalClose', handleProfileModalClose);
        };
    }, []);
    useEffect(() => {
        let roleObject = undefined;
        try {
            roleObject = JSON.parse(sessionStorage.getItem('policy') ?? '{}');
        }
        catch {
            // Session storage parse error, use undefined
        }
        const userRole = roleObject ? roleObject[0]?.name : null;
        setUserRole(userRole);
        setApplication(configState?.selectedProduct);
    }, [configState?.selectedProduct]);
    useEffect(() => {
        const fetchData = async () => {
            try {
                await Promise.all([
                    getSettings(),
                    getPageImage(),
                    getHeaderImage(),
                    getLayoutConfig(),
                    fetchThemeDetails()
                ]);
            }
            catch (error) {
                console.error('Failed to load initial layout data:', error);
            }
            finally {
                setIsLoading(false);
            }
        };
        fetchData();
    }, []);
    const getSettings = async () => {
        const response = await getCachedSettings();
        setConfigState((prevState) => ({
            ...prevState,
            settings: {
                ...prevState.settings,
                ...response
            }
        }));
    };
    const getPageImage = async () => {
        const response = await getCachedPageImage();
        setConfigState((prevState) => ({
            ...prevState,
            settings: {
                ...prevState.settings,
                page_image: response
            }
        }));
    };
    const getHeaderImage = async () => {
        const response = await getCachedHeaderImage();
        setConfigState((prevState) => ({
            ...prevState,
            settings: {
                ...prevState.settings,
                header_logo_image: response
            }
        }));
    };
    const applyConfigItem = (key, value) => {
        switch (key) {
            case 'RBAC_UI_HEADER':
                setShowHeader(value !== false);
                break;
            case 'RBAC_UI_FOOTER':
                setShowFooter(value !== false);
                break;
            case 'RBAC_UI_FOOTER_BRAND_NAME':
                if (value)
                    setFooterBrandName(value);
                break;
            case 'RBAC_UI_FOOTER_COPYRIGHT_TEXT':
                if (value)
                    setFooterCopyrightText(value);
                break;
            case 'RBAC_UI_PRIVACY_URL':
                if (value !== undefined)
                    setFooterPrivacyUrl(value);
                break;
            case 'RBAC_UI_FOOTER_TERMS_URL':
                if (value !== undefined)
                    setFooterTermsUrl(value);
                break;
            case 'RBAC_UI_COOKIE_URL':
                if (value !== undefined)
                    setFooterCookiesUrl(value);
                break;
        }
    };
    const getLayoutConfig = async () => {
        try {
            const bundle = await ConfigurationAPI.getConfigurations('UserExperience');
            for (const config of bundle.resource || []) {
                for (const item of config.items || []) {
                    applyConfigItem(item.key, item.value);
                }
            }
        }
        catch {
            // If config fetch fails, defaults from useState initializers are used
        }
    };
    useEffect(() => {
        if (configState?.selectedProduct === '') {
            // Get product from cookie or URL
            const productFromCookie = Cookies.get('Product Selected');
            if (productFromCookie) {
                setApplication(productFromCookie);
            }
            else {
                const pathParts = pathname.split('/').filter(Boolean);
                if (pathParts.length > 0) {
                    setApplication(pathParts[0]);
                }
            }
        }
        else {
            setApplication(configState?.selectedProduct);
        }
    }, [configState?.selectedProduct, pathname]);
    return (_jsxs(Box, { sx: {
            display: 'flex',
            flexDirection: 'column',
            height: '100vh',
            overflow: 'hidden'
        }, children: [_jsx(CssBaseline, {}), showHeader &&
                !isLoading &&
                pathname != '/' &&
                application != '' ? (_jsxs(AppBar, { position: "fixed", sx: {
                    background: `${configState?.settings?.header_background_color}`,
                    borderBottom: '1px solid #EBEBEB',
                    zIndex: 1200,
                    boxShadow: 'none',
                    height: 64,
                    overflow: 'visible'
                }, children: [_jsxs(Toolbar, { sx: {
                            padding: '20px !important',
                            minHeight: '64px !important',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            position: 'relative'
                        }, children: [_jsx(LogoAndProductName, {}), _jsxs(Box, { sx: {
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1
                                }, children: [application === 'associateProductivity' && (_jsx(Chip, { label: userRole, sx: {
                                            backgroundColor: '#37474f',
                                            marginRight: '16px',
                                            color: '#ffffff',
                                            fontFamily: 'Open Sans',
                                            fontSize: '13px',
                                            fontWeight: '400',
                                            height: '24px'
                                        } })), application === 'inventory' && (_jsx(Chip, { label: "Inventory Manager", sx: {
                                            backgroundColor: '#fff',
                                            border: '1px solid #d9d9d9',
                                            marginRight: '12px',
                                            color: '#000',
                                            fontFamily: 'Open Sans',
                                            fontSize: '12px',
                                            fontWeight: '600',
                                            height: '24px'
                                        } })), _jsxs(Box, { sx: { display: 'flex', gap: 2, pr: 2 }, children: [_jsx(PermissionGate, { action: [
                                                    RBAC_ACTIONS.RBAC_VIEW_NOTIFICATION
                                                ], children: _jsx(Box, { ref: notificationRef, children: _jsx(NotificationModal, {}) }) }), _jsx(Box, { ref: profileRef, children: _jsx(LogoutModal, {}) })] })] })] }), _jsxs(Box, { sx: {
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            height: '3px',
                            pointerEvents: 'none'
                        }, children: [renderIndicator(notificationRef, 'notification'), renderIndicator(profileRef, 'profile')] })] })) : null, !isLoading && (_jsx(Box, { component: "main", sx: {
                    flexGrow: 1,
                    pb: 0,
                    marginTop: showHeader && pathname != '/' && application != ''
                        ? '64px'
                        : '0px',
                    marginLeft: 0,
                    width: '100%',
                    height: showHeader && pathname != '/' && application != ''
                        ? 'calc(100vh - 64px)'
                        : '100vh',
                    overflowY: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: theme.transitions.create(['margin', 'width'], {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.enteringScreen
                    })
                }, children: !isLoading && (_jsxs(Container, { maxWidth: false, disableGutters: true, sx: {
                        backgroundColor: theme.palette.background.default,
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column'
                    }, role: "container-parent", children: [_jsx(Box, { sx: { flex: 1 }, children: children }), !isLoading && showFooter && (_jsxs(Box, { component: "footer", sx: {
                                display: 'flex',
                                flexDirection: 'row',
                                justifyContent: 'center',
                                alignItems: 'center',
                                backgroundColor: '#F8F8F8',
                                width: '100%',
                                padding: '16px',
                                marginTop: 'auto'
                            }, children: [_jsxs(Typography, { children: [_jsx(Box, { component: "span", sx: {
                                                fontSize: '16px',
                                                fontWeight: '700'
                                            }, children: footerBrandName }), ' | ' +
                                            footerCopyrightText +
                                            new Date().getFullYear() +
                                            constants.Copyright_text2] }), "\u00A0\u00A0\u00A0\u00A0", _jsx("a", { href: sanitizeUrl(footerPrivacyUrl), target: "_blank", rel: "noopener noreferrer", style: {
                                        textDecoration: 'none',
                                        color: 'inherit'
                                    }, children: _jsx(Typography, { sx: {
                                            '&:hover': {
                                                textDecoration: 'underline'
                                            }
                                        }, children: constants.privacy }) }), "\u00A0\u00A0\u00A0\u00A0", _jsx("a", { href: sanitizeUrl(footerTermsUrl), target: "_blank", rel: "noopener noreferrer", style: {
                                        textDecoration: 'none',
                                        color: 'inherit'
                                    }, children: _jsx(Typography, { sx: {
                                            '&:hover': {
                                                textDecoration: 'underline'
                                            }
                                        }, children: constants.terms_of_use }) }), "\u00A0\u00A0\u00A0\u00A0", _jsx("a", { href: sanitizeUrl(footerCookiesUrl), target: "_blank", rel: "noopener noreferrer", style: {
                                        textDecoration: 'none',
                                        color: 'inherit'
                                    }, children: _jsx(Typography, { sx: {
                                            '&:hover': {
                                                textDecoration: 'underline'
                                            }
                                        }, children: constants.cookies }) })] }))] })) }))] }));
}
